import React, { useState, useMemo, useEffect, useRef, useCallback, memo } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { supabase } from '@/integrations/supabase/client'
import mixpanel from 'mixpanel-browser'
import { 
  Calendar, 
  Home, 
  Users, 
  Building2,
  Settings,
  Package,
  Plus,
  Users as UsersIcon,
  Building,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Crown,
  Shield,
  User,
  Workflow,
  Bot,
  Camera,
  MessageSquare,
  Store,
  ShoppingCart,
  Tag,
  Truck,
  Receipt,
  GitBranch,
  Search,
  Check,
  MoreVertical,
  Brain,
  FileBarChart,
  Sparkles,
  Inbox,
  ChevronsUpDown,
  LogOut,
  Bell,
  Sun,
  Moon,
  Monitor,
  Palette,
  Send,
  BookOpen,
  BarChart2,
  ArrowUpLeft,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Sheet, SheetContent } from '@/components/ui/sheet'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Command, CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { useOrganizationContext } from '@/contexts/OrganizationContext'
import { type Organization, type OrganizationMember } from '@/hooks/useOrganizations'
import { useToast } from '@/hooks/use-toast'
import { useSidebar } from '@/components/ui/sidebar'
import { UploadLogoDialog } from '@/components/UploadLogoDialog'
import { useOrganizationLogo } from '@/hooks/useOrganizationLogo'
import { useAdmin } from '@/hooks/useAdmin'
import { useModuleEnabled } from '@/hooks/useModuleEnabled'
import { useProductAccess, PRODUCT_PAGES } from '@/hooks/useProductAccess'
import { usePermissionCheck } from '@/hooks/usePermissionCheck'
import { CreateOrganizationDialog } from '@/components/CreateOrganizationDialog'
import { useActiveModuleColor } from '@/contexts/ActiveModuleColorContext'
import { useAuth } from '@/hooks/useAuth'
import { useUserAvatar } from '@/hooks/useUserAvatar'
import { useProfileCompletion } from '@/hooks/useProfileCompletion'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useTheme } from '@/components/ThemeProvider'
import { NotificationsDropdown } from '@/components/NotificationsDropdown'
import { useNotifications } from '@/hooks/useNotifications'
import { openDocs } from '@/lib/crossDomainAuth'
import { buildLocalChatsUrl } from '@/lib/localChatsLink'

// Páginas que aparecem para TODOS os módulos (verdadeiramente universais)
const PAGES_FOR_ALL: string[] = [];

const MODULE_COLOR_MAP: Record<string, string> = {
  'fluity-chat':   '#6366f1',
  'fluity-sender': '#f59e0b',
  'fluity-crm':    '#10b981',
}

/** Rotas que vários módulos declaram; inferência default = Chat quando não há módulo selecionado. */
const SHARED_CROSS_MODULE_PREFIXES = ['/organizacoes', '/settings']

const MODULE_NAME_MAP: Record<string, string> = {
  'fluity-chat':   'Fluity Chat',
  'fluity-sender': 'Fluity Sender',
  'fluity-crm':    'Fluity CRM',
}

type PlatformProductRow = {
  product_key: string
  name: string
  image_path: string
  primary_color: string
}

const MODULES_STATIC: PlatformProductRow[] = [
  { product_key: 'fluity-chat',   name: 'Fluity Chat',   image_path: '', primary_color: '#6366f1' },
  { product_key: 'fluity-sender', name: 'Fluity Sender', image_path: '', primary_color: '#f59e0b' },
  { product_key: 'fluity-crm',    name: 'Fluity CRM',    image_path: '', primary_color: '#10b981' },
]

function getModuleDisplayNameFromKey(productKey: string): string {
  return MODULE_NAME_MAP[productKey] ?? productKey
}

/** Módulos que só aparecem na lista para usuários admin */
const MODULES_ADMIN_ONLY = ['fluity-sender', 'fluity-shop']


function getModuleColor(product_key: string, name: string, fallback: string): string {
  if (MODULE_COLOR_MAP[product_key]) return MODULE_COLOR_MAP[product_key]
  const lower = name.toLowerCase()
  if (lower.includes('sender')) return '#F59E0B'
  if (lower.includes('starter')) return '#2563EB'
  return fallback || '#8D76FF'
}

type BaseMenuItem = {
  title: string
  url: string
  icon: React.ComponentType<{ className?: string }>
  permission?: string
  end?: boolean
  badge?: string
  badgeColor?: string
}

type MenuItem = BaseMenuItem & {
  children?: BaseMenuItem[]
}

const menuItems: MenuItem[] = [
  { title: "Chats", url: "/chat", icon: MessageSquare },
  { title: "Performance", url: "/chat/performance", icon: BarChart2 },
]

// Componente de link individual memoizado - apenas este link re-renderiza quando sua rota muda
// O NavLink gerencia internamente a detecção de rota ativa usando useLocation
const MenuLink = memo(({ 
  item, 
  isCollapsed, 
  isMobile, 
  setOpenMobile 
}: {
  item: BaseMenuItem
  isCollapsed: boolean
  isMobile: boolean
  setOpenMobile: (open: boolean) => void
}) => {
  return (
    <NavLink
      to={item.url}
      end={item.end !== false}
      className={({ isActive }) => cn(
        "flex items-center gap-3 px-3 py-2 rounded-xl transition-colors duration-150 text-[#686881] font-normal",
        isCollapsed && !isMobile ? "justify-center" : "justify-start",
        isActive ? "bg-[#f7f7f7]" : "hover:bg-[#F0F0F0]"
      )}
      onClick={() => { mixpanel.track('sidebar_nav_clicked', { title: item.title, url: item.url }); isMobile && setOpenMobile(false) }}
    >
      <item.icon className={cn("shrink-0", isCollapsed && !isMobile ? "h-5 w-5" : "h-4 w-4")} />
      {(!isCollapsed || isMobile) && (
        <>
          <span className="text-sm flex-1">{item.title}</span>
          {item.badge && (
            <span
              className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full leading-none"
              style={item.badgeColor
                ? { background: item.badgeColor, color: 'white' }
                : { background: 'rgba(58,45,237,0.1)', color: '#1d4ed8' }
              }
            >
              {item.badge}
            </span>
          )}
        </>
      )}
    </NavLink>
  )
}, (prevProps, nextProps) => {
  return (
    prevProps.item.url === nextProps.item.url &&
    prevProps.item.title === nextProps.item.title &&
    prevProps.item.badge === nextProps.item.badge &&
    prevProps.isCollapsed === nextProps.isCollapsed &&
    prevProps.isMobile === nextProps.isMobile
  )
})

MenuLink.displayName = 'MenuLink'

// Componente de grupo expansível para items com sub-navegação
const MenuGroupItem = ({ 
  item, 
  isCollapsed, 
  isMobile, 
  setOpenMobile 
}: {
  item: MenuItem & { children: BaseMenuItem[] }
  isCollapsed: boolean
  isMobile: boolean
  setOpenMobile: (open: boolean) => void
}) => {
  const location = useLocation()
  const isChildActive = item.children.some(child => 
    location.pathname === child.url || location.pathname.startsWith(child.url + '/')
  )
  const isParentActive = location.pathname === item.url || location.pathname.startsWith(item.url + '/')
  const isAnyActive = isParentActive || isChildActive

  const [isOpen, setIsOpen] = useState(isAnyActive)

  useEffect(() => {
    if (isAnyActive) setIsOpen(true)
  }, [isAnyActive])

  if (isCollapsed && !isMobile) {
    return (
      <NavLink
        to={item.url}
        className={({ isActive }) => cn(
          "flex items-center justify-center px-3 py-2 rounded-xl transition-colors duration-150 text-[#686881] font-medium",
          (isActive || isChildActive) ? "bg-[#f7f7f7]" : "hover:bg-[#F0F0F0]"
        )}
      >
        <item.icon className="h-5 w-5 shrink-0" />
      </NavLink>
    )
  }

  return (
    <div>
      <div className="flex items-center gap-1">
        <NavLink
          to={item.url}
          className={({ isActive }) => cn(
            "flex items-center gap-3 px-3 py-2 rounded-xl transition-colors duration-150 flex-1 text-[#686881] font-medium",
            (isActive || isChildActive) ? "bg-[#f7f7f7]" : "hover:bg-[#F0F0F0]"
          )}
          onClick={() => {
            mixpanel.track('sidebar_nav_clicked', { title: item.title, url: item.url })
            isMobile && setOpenMobile(false)
            setIsOpen(true)
          }}
        >
          <item.icon className="h-4 w-4 shrink-0" />
          <span className="text-sm flex-1">{item.title}</span>
        </NavLink>
        <button
          onClick={() => setIsOpen(prev => !prev)}
          className="p-1 rounded transition-colors shrink-0 text-[#686881] hover:bg-[#F0F0F0]"
        >
          <ChevronDown className={cn("h-3 w-3 transition-transform duration-200", isOpen && "rotate-180")} />
        </button>
      </div>

      {isOpen && (
        <div className="ml-3 mt-1 space-y-0.5 pl-3 border-l border-sidebar-border">
          {item.children.map(child => (
            <NavLink
              key={child.url}
              to={child.url}
              className={({ isActive }) => cn(
                "flex items-center gap-3 px-3 py-1.5 rounded-xl transition-colors duration-150 text-sm text-[#686881] font-normal",
                isActive ? "bg-[#f7f7f7]" : "hover:bg-[#F0F0F0]"
              )}
              onClick={() => { mixpanel.track('sidebar_nav_clicked', { title: child.title, url: child.url }); isMobile && setOpenMobile(false) }}
            >
              <child.icon className="h-4 w-4 shrink-0" />
              <span>{child.title}</span>
            </NavLink>
          ))}
        </div>
      )}
    </div>
  )
}

MenuGroupItem.displayName = 'MenuGroupItem'

// Configurações globais ficam ocultas no menu lateral (acessíveis pelo menu de perfil)
const HIDDEN_IN_MODULE_URLS = ['/settings'];

// Componente memoizado para o menu - filtra itens pelo módulo ativo
const MenuItems = memo(({
  activeModuleKey,
  productsLoading,
  isCollapsed,
  isMobile,
  setOpenMobile,
  canShowGlobalSettings,
}: {
  activeModuleKey: string | null
  productsLoading: boolean
  isCollapsed: boolean
  isMobile: boolean
  setOpenMobile: (open: boolean) => void
  /** Alinhado a ProductBasedRoute adminOnly em /settings (admin da plataforma) */
  canShowGlobalSettings: boolean
}) => {
  const filteredItems = useMemo(() => {
    const modulePages = activeModuleKey ? (PRODUCT_PAGES[activeModuleKey] || []) : [];

    const checkUrlAccess = (url: string): boolean => {
      if (HIDDEN_IN_MODULE_URLS.includes(url)) return canShowGlobalSettings;
      if (PAGES_FOR_ALL.includes(url)) return true;
      if (productsLoading || !activeModuleKey) return false;
      return modulePages.includes(url);
    };

    return menuItems.filter(item => {
      if (item.children) {
        return checkUrlAccess(item.url) || item.children.some(child => checkUrlAccess(child.url));
      }
      return checkUrlAccess(item.url);
    }).map(item => {
      if (item.children) {
        return { ...item, children: item.children.filter(child => checkUrlAccess(child.url)) };
      }
      return item;
    });
  }, [activeModuleKey, productsLoading, canShowGlobalSettings]);

  return (
    <nav className="space-y-1">
      {filteredItems.map((item) =>
        item.children ? (
          <MenuGroupItem
            key={item.url}
            item={item as MenuItem & { children: BaseMenuItem[] }}
            isCollapsed={isCollapsed}
            isMobile={isMobile}
            setOpenMobile={setOpenMobile}
          />
        ) : (
          <MenuLink
            key={item.url}
            item={item}
            isCollapsed={isCollapsed}
            isMobile={isMobile}
            setOpenMobile={setOpenMobile}
          />
        )
      )}
    </nav>
  )
}, (prevProps, nextProps) => {
  return (
    prevProps.activeModuleKey === nextProps.activeModuleKey &&
    prevProps.productsLoading === nextProps.productsLoading &&
    prevProps.isCollapsed === nextProps.isCollapsed &&
    prevProps.isMobile === nextProps.isMobile &&
    prevProps.canShowGlobalSettings === nextProps.canShowGlobalSettings
  )
})

export function UnifiedSidebar() {
  const { state, toggleSidebar, isMobile, openMobile, setOpenMobile } = useSidebar()
  const isCollapsed = state === "collapsed"
  
  const { currentOrg, organizations, switchOrganization, refreshOrganizations, createOrganization, getOrganizationMembers } = useOrganizationContext()
  const navigate = useNavigate()
  const location = useLocation()
  const { toast } = useToast()
  const { hasAccess, isAdminMaster, userProducts, isLoading: productsLoading } = useProductAccess()
  const { isAdmin, loading: adminLoading } = useAdmin()
  const { hasPermission } = usePermissionCheck()

  // Mostrar TODAS as organizações na lista lateral (a lista tem scroll)
  // O botão "Ver todas" só aparece se realmente houver muitas organizações
  // e o usuário precisar de uma busca mais eficiente
  const MAX_ORGS_FOR_BUTTON = 11 // Só mostrar botão se houver mais de 11 organizações
  
  const visibleOrgs = useMemo(() => {
    // Mostrar TODAS as organizações na lista lateral
    // A lista tem scroll, então não há problema em mostrar todas
    if (!organizations || organizations.length === 0) return []
    return organizations
  }, [organizations])
  
  const hasMoreOrgs = useMemo(() => {
    // Só mostrar o botão "Ver todas" se houver muitas organizações
    // (mais de MAX_ORGS_FOR_BUTTON) para facilitar a busca
    return organizations && organizations.length > MAX_ORGS_FOR_BUTTON
  }, [organizations])

  // Debug: Log organizações para verificar (comentado para evitar re-renders desnecessários)
  // useEffect(() => {
  //   const currentOrgInList = organizations?.find(org => org.id === currentOrg?.id)
  //   const visibleOrgsIds = visibleOrgs.map(org => org.id)
  //   const isCurrentOrgVisible = currentOrg?.id ? visibleOrgsIds.includes(currentOrg.id) : false
  //   
  //   console.log('📋 [UnifiedSidebar] Organizações disponíveis:', {
  //     total: organizations?.length || 0,
  //     currentOrgId: currentOrg?.id,
  //     currentOrgName: currentOrg?.name,
  //     currentOrgInList: !!currentOrgInList,
  //     isCurrentOrgVisible,
  //     visibleOrgsCount: visibleOrgs.length,
  //     visibleOrgsIds,
  //     isAdminMaster,
  //     isAdmin,
  //   });
  // }, [organizations, currentOrg?.id, currentOrg?.name, visibleOrgs, isAdminMaster, isAdmin]);

  // Scroll automático para manter a organização selecionada visível
  // Usando useRef para evitar re-execuções desnecessárias
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  
  const [hoveredOrg, setHoveredOrg] = useState<string | null>(null)
  
  // Forçar atualização quando isMobile mudar para garantir que o menu seja recalculado
  // Removido useEffect vazio que estava causando re-renders desnecessários
  const [newOrgName, setNewOrgName] = useState("")
  const [newOrgDescription, setNewOrgDescription] = useState("")
  const [orgMembers, setOrgMembers] = useState<OrganizationMember[]>([])
  const [showMembersDialog, setShowMembersDialog] = useState(false)
  const [showUploadDialog, setShowUploadDialog] = useState(false)
  const [showOrgSwitcher, setShowOrgSwitcher] = useState(false)
  const [orgSearchQuery, setOrgSearchQuery] = useState("")
  const [createOrgDialogOpen, setCreateOrgDialogOpen] = useState(false)
  const [showNotificationsModal, setShowNotificationsModal] = useState(false)
  const orgListRef = useRef<HTMLDivElement>(null)
  
  useEffect(() => {
    // Só executar se o dialog de criar organização estiver fechado
    if (createOrgDialogOpen) return
    if (!currentOrg?.id || !orgListRef.current) return

    // Limpar timeout anterior se existir
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current)
    }

    // Aguardar um pouco para garantir que o DOM foi atualizado
    scrollTimeoutRef.current = setTimeout(() => {
      const scrollContainer = orgListRef.current
      if (!scrollContainer) return

      // Encontrar o elemento da organização selecionada usando querySelector
      const selectedElement = scrollContainer.querySelector(`[data-selected-org="${currentOrg.id}"]`) as HTMLElement
      if (!selectedElement) return

      // Verificar se o elemento selecionado está visível no container
      const containerRect = scrollContainer.getBoundingClientRect()
      const elementRect = selectedElement.getBoundingClientRect()

      const isVisible = 
        elementRect.top >= containerRect.top &&
        elementRect.bottom <= containerRect.bottom

      // Se não estiver visível, fazer scroll para centralizar
      if (!isVisible) {
        const elementOffsetTop = selectedElement.offsetTop - scrollContainer.offsetTop
        const containerHeight = scrollContainer.clientHeight
        const elementHeight = selectedElement.clientHeight

        // Calcular a posição para centralizar o elemento
        const scrollTo = elementOffsetTop - (containerHeight / 2) + (elementHeight / 2)
        
        scrollContainer.scrollTo({
          top: Math.max(0, scrollTo),
          behavior: 'smooth'
        })
      }
    }, 200) // Aumentar o delay para evitar execuções muito frequentes

    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current)
      }
    }
  }, [currentOrg?.id, organizations?.length, createOrgDialogOpen]) // Usar organizations?.length em vez de organizations para evitar re-renders quando apenas a referência muda
  
  const filteredOrgs = useMemo(() => {
    if (!organizations) return []
    const query = orgSearchQuery.toLowerCase().trim()
    const list = query
      ? organizations.filter(org =>
          org.name?.toLowerCase().includes(query) ||
          (org.slug?.toLowerCase() || '').includes(query)
        )
      : organizations

    const INACTIVE = ['suspended', 'canceled']
    const active   = list.filter(o => !INACTIVE.includes(o.status ?? ''))
    const inactive = list.filter(o =>  INACTIVE.includes(o.status ?? ''))
    return { active, inactive }
  }, [organizations, orgSearchQuery])
  
  const { data: isLanchesEnabled } = useModuleEnabled(
    currentOrg?.id,
    'fluity_lanches'
  )
  
  // Sempre mostra todos os módulos; os bloqueados exibem cadeado e modo visualização
  const platformProducts = useMemo(() => MODULES_STATIC, [])

  const [selectedModuleKey, _setSelectedModuleKey] = useState<string | null>(
    () => localStorage.getItem('fluity_active_module')
  )
  const setSelectedModuleKey = useCallback((key: string | null) => {
    _setSelectedModuleKey(key)
    if (key) {
      localStorage.setItem('fluity_active_module', key)
    } else {
      localStorage.removeItem('fluity_active_module')
    }
  }, [])

  // Se o usuário escolheu um módulo no seletor e a rota atual pertence a esse módulo, usar essa escolha
  const pathBelongsToProduct = useCallback((path: string, productKey: string) => {
    const pages = PRODUCT_PAGES[productKey] || []
    return pages.some(page => path === page || path.startsWith(page + '/'))
  }, [])

  // Inferir módulo pela rota; em ambíguos respeita o módulo já selecionado (ex.: Agenda → Organizações).
  const inferredModuleKeyFromPath = useMemo(() => {
    const currentPath = location.pathname
    const matches: { productKey: string; pageCount: number }[] = []
    for (const [productKey, pages] of Object.entries(PRODUCT_PAGES)) {
      if (pages.some(page => currentPath === page || currentPath.startsWith(page + '/'))) {
        matches.push({ productKey, pageCount: pages.length })
      }
    }
    if (matches.length === 0) return null
    if (matches.length === 1) return matches[0].productKey
    const onShopPath = currentPath === '/shop' || currentPath.startsWith('/shop/')
    if (onShopPath && matches.some(m => m.productKey === 'fluity-shop')) return 'fluity-shop'

    const matchKeys = new Set(matches.map((m) => m.productKey))
    if (selectedModuleKey && matchKeys.has(selectedModuleKey)) {
      return selectedModuleKey
    }

    const isSharedUniversal = SHARED_CROSS_MODULE_PREFIXES.some(
      (base) => currentPath === base || currentPath.startsWith(`${base}/`),
    )
    if (isSharedUniversal && matchKeys.has('fluity-starter')) {
      return 'fluity-starter'
    }

    matches.sort((a, b) => {
      if (a.pageCount !== b.pageCount) return a.pageCount - b.pageCount
      const aIsStarter = a.productKey === 'fluity-starter' ? 1 : 0
      const bIsStarter = b.productKey === 'fluity-starter' ? 1 : 0
      return aIsStarter - bIsStarter
    })
    return matches[0].productKey
  }, [location.pathname, selectedModuleKey])

  // Módulo ativo: 1) escolha explícita do usuário; 2) inferido pela rota; 3) primeiro módulo habilitado
  const activeModuleKey = useMemo(() => {
    const inferred = inferredModuleKeyFromPath
    if (selectedModuleKey) return selectedModuleKey
    if (inferred) return inferred
    return platformProducts[0]?.product_key ?? null
  }, [location.pathname, selectedModuleKey, inferredModuleKeyFromPath, platformProducts])

  // Manter selectedModuleKey em sync com a rota: ao mudar de página (ex.: menu), atualizar o seletor na hora
  useEffect(() => {
    const inferred = inferredModuleKeyFromPath
    if (!inferred) return
    const pathBelongsToSelected = selectedModuleKey && pathBelongsToProduct(location.pathname, selectedModuleKey)
    if (pathBelongsToSelected) return
    setSelectedModuleKey(inferred)
  }, [location.pathname, inferredModuleKeyFromPath, selectedModuleKey, pathBelongsToProduct])

  const activeModule = useMemo(() => {
    return platformProducts.find(p => p.product_key === activeModuleKey) ?? platformProducts[0] ?? null
  }, [activeModuleKey, platformProducts])

  // Para o botão do seletor: usar sempre activeModuleKey para rótulo/cor, com fallback se o produto não estiver em platformProducts
  const displayModule = useMemo(() => {
    if (activeModule && activeModule.product_key === activeModuleKey) return activeModule
    if (!activeModuleKey) return null
    const displayName = getModuleDisplayNameFromKey(activeModuleKey)
    const color = getModuleColor(activeModuleKey, displayName, '#8D76FF')
    return { product_key: activeModuleKey, name: displayName, image_path: '', primary_color: color }
  }, [activeModule, activeModuleKey])

  const { setColor: setActiveModuleColor } = useActiveModuleColor()
  const { notifications } = useNotifications()
  const unreadNotificationsCount = notifications.filter(n => !n.is_read).length
  const { user, session, signOut } = useAuth()
  const { avatarUrl } = useUserAvatar()
  const { isProfileComplete, loading: profileCompletionLoading } = useProfileCompletion()
  const showIncompleteProfileBadge = !profileCompletionLoading && isProfileComplete === false
  const { theme, setTheme } = useTheme()
  const [showProfile, setShowProfile] = useState(false)
  const [showThemeSubmenu, setShowThemeSubmenu] = useState(false)
  const themeHoverTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const handleSignOut = useCallback(async () => {
    mixpanel.track('sidebar_logout_clicked')
    setShowProfile(false)
    await signOut()
  }, [signOut])

  const handleOpenLocalChats = useCallback(() => {
    mixpanel.track('sidebar_profile_item_clicked', { item: 'abrir_chats_local' })
    setShowProfile(false)
    window.open(buildLocalChatsUrl(session, currentOrg?.id), '_blank', 'noopener,noreferrer')
  }, [session, currentOrg?.id])

  useEffect(() => {
    if (!showProfile) {
      setShowThemeSubmenu(false)
    }
  }, [showProfile])

  useEffect(() => {
    if (displayModule) {
      setActiveModuleColor(getModuleColor(displayModule.product_key, displayModule.name, displayModule.primary_color))
    }
  }, [displayModule?.product_key, displayModule?.primary_color])

  // Removido getNavCls - agora usamos currentPath diretamente no JSX para evitar re-renders desnecessários

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'owner': return <Crown className="h-3 w-3 text-yellow-500 dark:text-yellow-400" />
      case 'admin': return <Shield className="h-3 w-3 text-blue-500 dark:text-blue-400" />
      default: return <User className="h-3 w-3 text-muted-foreground" />
    }
  }

  const handleCreateOrganization = useCallback(async () => {
    if (!newOrgName.trim()) return
    
    try {
      const slug = newOrgName
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, '')
        .replace(/\s+/g, '-')
        .trim()
      
      const result = await createOrganization(newOrgName, slug)
      
      if (result) {
        toast({
          title: "Organização criada!",
          description: `${newOrgName} foi criada com sucesso.`,
        })
        setNewOrgName("")
        setNewOrgDescription("")
        setCreateOrgDialogOpen(false)
        
        const wasFirstOrg = organizations.length === 0
        await refreshOrganizations()
        
        if (wasFirstOrg) {
          window.dispatchEvent(new CustomEvent('organization-created', { detail: { orgId: result.id } }));
          
          setTimeout(async () => {
            try {
              const { data: products, error } = await supabase
                .from('organization_products')
                .select('product_key')
                .eq('org_id', result.id)
                .limit(1);
              
              if (error || !products || products.length === 0) {
                navigate('/product-selection');
              }
            } catch (err) {
              console.error('Erro ao verificar produtos após criar organização:', err);
              navigate('/product-selection');
            }
          }, 1500);
        }
      }
    } catch (error: any) {
      console.error('Error in handleCreateOrganization:', error)
      toast({
        title: "Erro ao criar organização",
        description: error.message || "Erro desconhecido",
        variant: "destructive",
      })
    }
  }, [newOrgName, createOrganization, toast, organizations.length, refreshOrganizations, navigate])
  
  const handleDialogOpenChange = useCallback((open: boolean) => {
    setCreateOrgDialogOpen(open)
    if (!open) {
      // Limpar campos quando o dialog fechar
      setNewOrgName("")
      setNewOrgDescription("")
    }
  }, [])
  
  const handleCancel = useCallback(() => {
    setCreateOrgDialogOpen(false)
    setNewOrgName("")
    setNewOrgDescription("")
  }, [])
  
  const handleOrgNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setNewOrgName(value)
  }, [])
  
  const handleOrgDescriptionChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value
    setNewOrgDescription(value)
  }, [])

  // Memoizar funções auxiliares para evitar recriações
  const memoizedGetInitials = useCallback((name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }, [])

  const memoizedGetRoleLabel = useCallback((role: string) => {
    switch (role) {
      case 'owner': return 'Proprietário'
      case 'admin': return 'Administrador'
      case 'member': return 'Membro'
      default: return role
    }
  }, [])

  const memoizedGetPlanBadgeVariant = useCallback((planName: string) => {
    const planLower = planName.toLowerCase()
    if (planLower.includes('gratuito') || planLower.includes('free')) return 'outline'
    if (planLower.includes('premium') || planLower.includes('pro')) return 'default'
    return 'secondary'
  }, [])

  // Memoizar só o resto do sidebar (menu, org, perfil). O seletor de módulo é renderizado fora para atualizar sempre.
  const SidebarRestContent = useMemo(() => {
    return (
    <>
        <div className="flex-1 flex flex-col min-h-0 overflow-y-auto">
          <div className="px-3 pt-2 pb-3 will-change-auto">
            <MenuItems
              activeModuleKey={activeModuleKey}
              productsLoading={productsLoading}
              isCollapsed={isCollapsed}
              isMobile={isMobile}
              setOpenMobile={setOpenMobile}
              canShowGlobalSettings={isAdmin && !adminLoading}
            />
          </div>

          {/* Voltar para Fluity */}
          <div className={cn("px-3 pb-3 flex-shrink-0", isCollapsed && !isMobile && "px-2")}>
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <a
                  href={`https://www.fluity.com.br/chat${currentOrg?.id ? `?orgId=${currentOrg.id}` : ''}`}
                  onClick={() => mixpanel.track('sidebar_nav_clicked', { title: 'Voltar para Fluity', url: `https://www.fluity.com.br/chat${currentOrg?.id ? `?orgId=${currentOrg.id}` : ''}` })}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2 rounded-xl transition-colors duration-150 text-[#686881] hover:bg-[#F0F0F0] w-full",
                    isCollapsed && !isMobile ? "justify-center" : "justify-start"
                  )}
                >
                  <ArrowUpLeft className={cn("shrink-0", isCollapsed && !isMobile ? "h-5 w-5" : "h-4 w-4")} />
                  {(!isCollapsed || isMobile) && (
                    <span className="text-sm">Voltar para Fluity</span>
                  )}
                </a>
              </TooltipTrigger>
              {isCollapsed && !isMobile && (
                <TooltipContent side="right">Voltar para Fluity</TooltipContent>
              )}
            </Tooltip>
          </div>
        </div>

        {/* Org Switcher */}
        <div className={cn("px-5 pb-4 flex-shrink-0", isCollapsed && !isMobile && "px-2 pb-2")}>
          <Popover open={showOrgSwitcher} onOpenChange={(open) => { if (open) mixpanel.track('sidebar_org_switcher_opened'); setShowOrgSwitcher(open) }}>
            <PopoverTrigger asChild>
              <button className={cn(
                "group flex items-center gap-3 w-full rounded-2xl transition-colors duration-200 text-left outline-none",
                "bg-[#f7f7f7] hover:bg-[#F0F0F0]",
                isCollapsed && !isMobile ? "justify-center p-2 h-12 w-12" : "pl-2 pr-4 h-12"
              )}>
                <div className="h-8 w-8 rounded-xl flex items-center justify-center shrink-0 overflow-hidden bg-primary text-primary-foreground text-xs font-bold">
                  {currentOrg?.logo_url ? (
                    <img src={currentOrg.logo_url} alt={currentOrg.name} className="h-full w-full object-cover rounded-xl" />
                  ) : (
                    <span>{currentOrg ? memoizedGetInitials(currentOrg.name) : 'O'}</span>
                  )}
                </div>
                {(!isCollapsed || isMobile) && (
                  <>
                    <span className="text-xs font-semibold text-black truncate flex-1">
                      {currentOrg?.name || 'Organização'}
                    </span>
                    <ChevronsUpDown className="h-3.5 w-3.5 text-slate-500 shrink-0" />
                  </>
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent side="top" align="start" sideOffset={8}
              className="w-64 p-0 shadow-2xl border border-border/60 rounded-xl overflow-hidden bg-popover z-[200]"
              onOpenAutoFocus={(e) => e.preventDefault()}>
              <div className="px-3 pt-3 pb-2 border-b border-border/50">
                <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-widest">Organizações</p>
              </div>
              <Command>
                <CommandInput placeholder="Buscar organização..." value={orgSearchQuery} onValueChange={setOrgSearchQuery} />
                <CommandList className="max-h-[300px]">
                  <CommandEmpty>Nenhuma encontrada.</CommandEmpty>
                  {/* Orgs ativas */}
                  <CommandGroup>
                    {filteredOrgs.active.map((org) => (
                      <CommandItem key={org.id}
                        onSelect={() => { mixpanel.track('sidebar_org_changed', { org_id: org.id, org_name: org.name }); switchOrganization(org); setShowOrgSwitcher(false); setOrgSearchQuery("") }}
                        className="flex items-center gap-3 p-3 cursor-pointer">
                        <div className={cn("h-8 w-8 rounded-lg flex items-center justify-center font-bold text-xs shrink-0",
                          currentOrg?.id === org.id ? "bg-primary text-white" : "bg-muted text-foreground")}>
                          {org.logo_url ? <img src={org.logo_url} alt={org.name} className="h-full w-full object-cover rounded-lg" /> : <span>{memoizedGetInitials(org.name)}</span>}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm truncate">{org.name}</div>
                          <div className="text-xs text-muted-foreground">{memoizedGetRoleLabel(org.role)}</div>
                        </div>
                        {currentOrg?.id === org.id && <Check className="h-4 w-4 text-primary shrink-0" />}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                  {/* Orgs inativas */}
                  {filteredOrgs.inactive.length > 0 && (
                    <>
                      <div className="px-3 py-1.5 border-t border-border/50">
                        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">Inativas</p>
                      </div>
                      <CommandGroup>
                        {filteredOrgs.inactive.map((org) => (
                          <CommandItem key={org.id}
                            onSelect={() => { mixpanel.track('sidebar_org_changed', { org_id: org.id, org_name: org.name }); switchOrganization(org); setShowOrgSwitcher(false); setOrgSearchQuery("") }}
                            className="flex items-center gap-3 p-3 cursor-pointer opacity-60">
                            <div className={cn("h-8 w-8 rounded-lg flex items-center justify-center font-bold text-xs shrink-0",
                              currentOrg?.id === org.id ? "bg-primary text-white" : "bg-muted text-foreground")}>
                              {org.logo_url ? <img src={org.logo_url} alt={org.name} className="h-full w-full object-cover rounded-lg" /> : <span>{memoizedGetInitials(org.name)}</span>}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm truncate">{org.name}</div>
                              <div className="text-xs text-muted-foreground flex items-center gap-1.5">
                                {memoizedGetRoleLabel(org.role)}
                                <span className="inline-block w-1 h-1 rounded-full bg-muted-foreground/40" />
                                <span className="text-red-400 font-medium">
                                  {org.status === 'suspended' ? 'Suspensa' : 'Cancelada'}
                                </span>
                              </div>
                            </div>
                            {currentOrg?.id === org.id && <Check className="h-4 w-4 text-primary shrink-0" />}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </>
                  )}
                </CommandList>
              </Command>
              <div className="p-2 border-t border-border/50">
                <button onClick={() => { mixpanel.track('sidebar_org_create_clicked'); setShowOrgSwitcher(false); setCreateOrgDialogOpen(true) }}
                  className="flex items-center gap-2 w-full px-2.5 py-2 rounded-lg hover:bg-accent transition-colors text-sm">
                  <Plus className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Nova organização</span>
                </button>
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Profile */}
        <div className={cn("border-t border-border flex-shrink-0", isCollapsed && !isMobile ? "px-2 py-2" : "px-3 py-3")}>
          <Popover open={showProfile} onOpenChange={(open) => { if (open) mixpanel.track('sidebar_profile_menu_opened'); setShowProfile(open) }}>
            <PopoverTrigger asChild>
              <button className={cn(
                "flex items-center gap-3 w-full rounded-xl transition-colors outline-none text-left",
                "hover:bg-[#F0F0F0]",
                isCollapsed && !isMobile ? "justify-center p-2 h-10 w-10" : "px-2 py-2"
              )}>
                {showIncompleteProfileBadge ? (
                  <Tooltip delayDuration={400}>
                    <TooltipTrigger asChild>
                      <div className="relative shrink-0">
                        <Avatar className="h-8 w-8" key={avatarUrl}>
                          <AvatarImage src={avatarUrl || undefined} alt="Avatar" />
                          <AvatarFallback className="bg-primary/10 text-xs font-medium">
                            {user?.email?.charAt(0).toUpperCase() || 'U'}
                          </AvatarFallback>
                        </Avatar>
                        {unreadNotificationsCount > 0 && (
                          <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-orange-500 ring-2 ring-background" />
                        )}
                        <span
                          className={cn(
                            "absolute -top-0.5 h-2.5 w-2.5 rounded-full bg-amber-500 ring-2 ring-background",
                            unreadNotificationsCount > 0 ? "-left-0.5" : "-right-0.5"
                          )}
                          aria-hidden
                        />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-[220px] text-center">
                      <p className="font-semibold">Finalize seu cadastro</p>
                      <p className="text-muted-foreground text-xs mt-0.5">Quase lá! Falta só um cadinho para seu perfil ficar 100%. Clique e vá em Meu Perfil para completar. 😊</p>
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  <div className="relative shrink-0">
                    <Avatar className="h-8 w-8" key={avatarUrl}>
                      <AvatarImage src={avatarUrl || undefined} alt="Avatar" />
                      <AvatarFallback className="bg-primary/10 text-xs font-medium">
                        {user?.email?.charAt(0).toUpperCase() || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    {unreadNotificationsCount > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-orange-500 ring-2 ring-background" />
                    )}
                  </div>
                )}
                {(!isCollapsed || isMobile) && (
                  <span className="text-sm font-semibold text-[#020817] truncate flex-1">
                    {user?.user_metadata?.name || user?.email?.split('@')[0] || 'Usuário'}
                  </span>
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent
              side="top"
              align="start"
              sideOffset={8}
              className="w-[236px] p-0 shadow-2xl border-0 rounded-2xl overflow-hidden bg-popover z-[200]"
              onOpenAutoFocus={(e) => e.preventDefault()}
            >
              {showIncompleteProfileBadge && (
                <div className="px-3 py-2 bg-amber-50 dark:bg-amber-950/40 border-b border-amber-200/50 dark:border-amber-800/50">
                  <p className="text-xs font-medium text-amber-800 dark:text-amber-200">Finalize seu cadastro</p>
                  <p className="text-[11px] text-amber-700/90 dark:text-amber-300/90 mt-0.5">Clique em Meu Perfil abaixo para completar.</p>
                </div>
              )}
              {/* Items */}
              <div className="p-2 space-y-0.5">
                {/* Perfil */}
                <button
                  onClick={() => { mixpanel.track('sidebar_profile_item_clicked', { item: 'meu_perfil' }); navigate('/profile'); setShowProfile(false) }}
                  className="flex items-center gap-3 w-full px-2.5 py-2 rounded-lg hover:bg-[#F0F0F0] transition-colors text-sm text-[#686881]"
                >
                  <User className="h-4 w-4 text-[#686881] shrink-0" />
                  <span>Meu Perfil</span>
                </button>

                {hasAccess('/settings') && (
                  <button
                    onClick={() => { mixpanel.track('sidebar_profile_item_clicked', { item: 'configuracoes' }); navigate('/settings'); setShowProfile(false) }}
                    className="flex items-center gap-3 w-full px-2.5 py-2 rounded-lg hover:bg-[#F0F0F0] transition-colors text-sm text-[#686881]"
                  >
                    <Settings className="h-4 w-4 text-[#686881] shrink-0" />
                    <span>Configurações</span>
                  </button>
                )}

                <button
                  onClick={() => {
                    mixpanel.track('sidebar_profile_item_clicked', { item: 'docs' })
                    setShowProfile(false)
                    openDocs(session)
                  }}
                  className="flex items-center gap-3 w-full px-2.5 py-2 rounded-lg hover:bg-[#F0F0F0] transition-colors text-sm text-[#686881]"
                >
                  <BookOpen className="h-4 w-4 text-[#686881] shrink-0" />
                  <span>Documentação</span>
                </button>

                {import.meta.env.DEV && currentOrg?.id && session?.access_token && session?.refresh_token && (
                  <button
                    onClick={handleOpenLocalChats}
                    className="flex items-center gap-3 w-full px-2.5 py-2 rounded-lg hover:bg-[#F0F0F0] transition-colors text-sm text-[#686881]"
                  >
                    <MessageSquare className="h-4 w-4 text-[#686881] shrink-0" />
                    <span>Abrir Chats local</span>
                  </button>
                )}

                {/* Tema — submenu ao hover */}
                <Popover open={showThemeSubmenu} onOpenChange={setShowThemeSubmenu}>
                  <PopoverTrigger asChild>
                    <button
                      onMouseEnter={() => {
                        if (themeHoverTimeoutRef.current) clearTimeout(themeHoverTimeoutRef.current)
                        setShowThemeSubmenu(true)
                      }}
                      onMouseLeave={() => {
                        themeHoverTimeoutRef.current = setTimeout(() => setShowThemeSubmenu(false), 150)
                      }}
                      className="flex items-center gap-3 w-full px-2.5 py-2 rounded-lg hover:bg-[#F0F0F0] transition-colors text-sm text-[#686881]"
                    >
                      <Palette className="h-4 w-4 text-[#686881] shrink-0" />
                      <span className="flex-1 text-left">Tema</span>
                      <ChevronRight className="h-3.5 w-3.5 text-[#686881] shrink-0" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    side="right"
                    align="start"
                    sideOffset={4}
                    className="w-36 p-2"
                    onMouseEnter={() => {
                      if (themeHoverTimeoutRef.current) clearTimeout(themeHoverTimeoutRef.current)
                    }}
                    onMouseLeave={() => {
                      themeHoverTimeoutRef.current = setTimeout(() => setShowThemeSubmenu(false), 150)
                    }}
                    onOpenAutoFocus={(e) => e.preventDefault()}
                  >
                    <div className="space-y-0.5">
                      {([
                        { value: 'light', icon: Sun, label: 'Claro' },
                        { value: 'gray', icon: Monitor, label: 'Neutro' },
                        { value: 'dark', icon: Moon, label: 'Escuro' },
                      ] as const).map(t => (
                        <button
                          key={t.value}
                          onClick={() => { mixpanel.track('sidebar_theme_changed', { theme: t.value }); setTheme(t.value); setShowThemeSubmenu(false) }}
                          className={cn(
                            "flex items-center gap-2.5 w-full px-2.5 py-2 rounded-lg text-sm transition-colors",
                            theme === t.value
                              ? "bg-[#2563EB]/15 text-[#2563EB] font-medium"
                              : "hover:bg-[#F0F0F0] text-[#686881]"
                          )}
                        >
                          <t.icon className="h-3.5 w-3.5 shrink-0" />
                          {t.label}
                          {theme === t.value && <Check className="h-3 w-3 ml-auto shrink-0" />}
                        </button>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>

                {/* Notificações */}
                <button
                  onClick={() => { mixpanel.track('sidebar_notifications_opened'); setShowProfile(false); setShowNotificationsModal(true) }}
                  className="flex items-center gap-3 w-full px-2.5 py-2 rounded-lg hover:bg-[#F0F0F0] transition-colors text-sm text-[#686881]"
                >
                  <div className="relative shrink-0">
                    <Bell className="h-4 w-4 text-[#686881]" />
                    {unreadNotificationsCount > 0 && (
                      <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-orange-500" />
                    )}
                  </div>
                  <span className="flex-1 text-left">Notificações</span>
                </button>

              </div>

              {/* Sair */}
              <div className="border-t border-border/50 p-2">
                <button
                  onClick={handleSignOut}
                  className="flex items-center gap-3 w-full px-2.5 py-2 rounded-lg hover:bg-destructive/10 transition-colors text-sm text-destructive"
                >
                  <LogOut className="h-4 w-4 shrink-0" />
                  <span>Sair</span>
                </button>
              </div>
            </PopoverContent>
          </Popover>
        </div>

    </>
    )
  }, [
    activeModuleKey,
    isMobile,
    isCollapsed,
    isAdmin,
    adminLoading,
    currentOrg?.id,
    currentOrg?.name,
    currentOrg?.logo_url,
    organizations?.length,
    visibleOrgs.length,
    hasMoreOrgs,
    showOrgSwitcher,
    orgSearchQuery,
    filteredOrgs.active.length + filteredOrgs.inactive.length,
    hoveredOrg,
    userProducts.length,
    productsLoading,
    isLanchesEnabled,
    memoizedGetInitials,
    memoizedGetRoleLabel,
    memoizedGetPlanBadgeVariant,
    isAdminMaster,
    hasAccess,
    showProfile,
    showThemeSubmenu,
    showNotificationsModal,
    unreadNotificationsCount,
    avatarUrl,
    user?.email,
    user?.user_metadata?.name,
    theme,
    handleSignOut,
    handleOpenLocalChats,
    showIncompleteProfileBadge,
    session,
  ])

  // Wrapper do sidebar
  const sidebarWrapper = (
    <div className={cn(
      "h-full bg-sidebar-background border-r border-sidebar-border flex flex-col transition-all duration-300 will-change-auto",
      isCollapsed && !isMobile ? "w-16" : "w-64"
    )}>
      <div className={cn(
        "flex-shrink-0 flex items-center border-b border-sidebar-border",
        isCollapsed && !isMobile ? "justify-center px-3 py-5" : "px-4 py-6"
      )}>
        <img
          src="/logo-small.svg"
          alt="Fluity"
          className={cn(
            "object-contain",
            isCollapsed && !isMobile ? "h-5 w-5" : "h-5 w-auto"
          )}
        />
      </div>
      {SidebarRestContent}
    </div>
  )

  const content = isMobile ? (
    <>
      <Sheet open={openMobile} onOpenChange={setOpenMobile}>
        <SheetContent side="left" className="p-0 w-[320px] sm:w-[320px] border-none">
          {sidebarWrapper}
        </SheetContent>
      </Sheet>
    </>
  ) : (
    <div className="fixed left-0 top-0 h-screen hidden md:flex z-[60]">
      {sidebarWrapper}
      {/* Botão toggle na divisória */}
      <button
        onClick={() => { mixpanel.track('sidebar_toggle_clicked', { collapsed: !isCollapsed }); toggleSidebar() }}
        aria-label="Toggle sidebar"
        style={{ top: '20px' }}
        className="absolute right-0 translate-x-1/2 z-[200] flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-border bg-background text-muted-foreground shadow-md transition-all duration-200 hover:border-primary hover:text-primary hover:shadow-lg hover:scale-110 active:scale-95"
      >
        {isCollapsed
          ? <ChevronRight className="h-3.5 w-3.5" />
          : <ChevronLeft className="h-3.5 w-3.5" />
        }
      </button>
    </div>
  )


  return (
    <>
      {content}
      <NotificationsDropdown asModal open={showNotificationsModal} onOpenChange={setShowNotificationsModal} />
      <CreateOrganizationDialog
        open={createOrgDialogOpen}
        onOpenChange={handleDialogOpenChange}
        orgName={newOrgName}
        orgDescription={newOrgDescription}
        onOrgNameChange={handleOrgNameChange}
        onOrgDescriptionChange={handleOrgDescriptionChange}
        onCreate={handleCreateOrganization}
        onCancel={handleCancel}
      />
      <Dialog open={showMembersDialog} onOpenChange={setShowMembersDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Membros da Organização</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {orgMembers.map((member) => (
              <div key={member.id} className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center">
                    <User className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="font-medium text-sm">{member.name || 'Usuário'}</div>
                    <div className="text-xs text-muted-foreground">{member.email}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {getRoleIcon(member.role)}
                  <Badge variant="secondary" className="text-xs">{memoizedGetRoleLabel(member.role)}</Badge>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
      <UploadLogoDialog open={showUploadDialog} onOpenChange={setShowUploadDialog} />
    </>
  )
}
